import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import { INavItems } from "@/utils/interfaces";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";
import Button from "@/common/Button";
import { Popover, Portal, Transition } from "@headlessui/react";
import { IoChevronDown, IoHeart } from "react-icons/io5";
import { usePopper } from "react-popper";
import { RxHamburgerMenu } from "react-icons/rx";
import Drawer from "@/common/Drawer";
import Avatar from "@/components/Avatar";
import { IoCartOutline } from "react-icons/io5";
import { useCartStore } from "@/store/cart";
import { useWishlistStore } from "@/store/wishlist";

const NavDropDown = ({ item, subLink }: any) => {
  const popperElRef = useRef<any>(null);
  const popoverButtonRef = useRef<any>(null);
  const [referenceElement, setReferenceElement] = useState<any>(null);
  const [popperElement, setPopperElement] = useState<any>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "bottom-start",
    modifiers: [
      {
        name: "flip",
        options: {
          fallbackPlacements: ["bottom", "bottom", "auto"],
        },
      },
      {
        name: "preventOverflow",
        options: {
          rootBoundary: "document",
          padding: 4,
        },
      },
    ],
    strategy: "absolute",
  });

  const handleMouseToogle = () => {
    if (popoverButtonRef.current) {
      popoverButtonRef.current.click();
    }
  };

  return (
    <Popover
      className="relative"
      onMouseEnter={handleMouseToogle}
      onMouseLeave={handleMouseToogle}
    >
      {({ open }) => (
        <>
          <div
            className={clsx("relative group xl:px-[20px] xl:py-[4px] lg:px-[5px] lg:py-[4px] flex items-center gap-1 whitespace-nowrap transition-colors duration-300 hover:bg-white hover:bg-opacity-20 rounded-[6px] focus:rounded-[20px] focus:outline-none",
              {
                "text-white border-b-4 border-transparent hover:border-[#3586FF]":
                  !item.isActive && !subLink,
                "text-[#3586FF] border-b-4 border-[#3586FF]":
                  item.isActive && !subLink,
                "bg-white bg-opacity-20": open && !subLink,
              }
            )}
            ref={setReferenceElement}
          >
            {item.link ? (
              <Link
                href={item.link}
                className="whitespace-nowrap font-Gordita-Medium md:text-[14px]"
              >
                {item.name}
              </Link>
            ) : (
              <span className="whitespace-nowrap cursor-pointer  md:text-[14px]">
                {item.name}
              </span>
            )}
            <Popover.Button
              as="button"
              ref={popoverButtonRef}
              aria-label="Expand Menu"
              className={" border-none focus:outline-none"}
            >
              <IoChevronDown
                className={clsx("transition-transform duration-200", {
                  "ml-1 text-white text-base cursor-pointer": !item.isActive && !subLink,
                  "text-[#3586FF]": item.isActive && !subLink,
                  "rotate-180": open,
                })}
              />
            </Popover.Button>
          </div>


          <div className="hidden lg:block ">
            {referenceElement && (
              <Portal>
                <div
                  ref={popperElRef}
                  style={styles.popper}
                  className="absolute top-[20px] pr-[20px] z-[999999] md:mt-[0px] mt-[15px] md:pl-[0px] pl-[25px]"
                  {...attributes.popper}
                >
                  <Transition
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                    beforeEnter={() => setPopperElement(popperElRef.current)}
                    afterLeave={() => setPopperElement(null)}
                  >
                    {popperElement && (
                      <Popover.Panel
                        className={`bg-white z-[99999] text-black font-Gordita-Medium shadow-lg min-w-[180px] rounded-md py-3 `}
                      >
                        {item.subLink && (
                          <WebNavigationMenu
                            items={item.subLink}
                            subLink={true}
                          />
                        )}
                      </Popover.Panel>
                    )}
                  </Transition>
                </div>
              </Portal>

            )}
          </div>
        </>
      )}
    </Popover>
  );
};

interface INavigationMenuProps {
  items: Array<INavItems>;
  subLink: boolean;
}

const WebNavigationMenu = ({ items, subLink }: INavigationMenuProps) => {
  return (
    <>
      {items.map((item, index) => (
        <div key={`${index}-${item.name}-link`}>
          <>
            {item.link && !item.subLink ? (
              <Link
                href={item.link}
                onClick={() => {
                  item.onClick &&
                    typeof onclick === "function" &&
                    item.onClick();
                }}
              >
                <div
                  className={clsx(
                    "md:text-[14px]",
                    {
                      "xl:px-[24px] xl:py-[8px] lg:px-[15px] lg:py-[4px] font-Gordita-Medium whitespace-nowrap hover:bg-white hover:bg-opacity-20 hover:rounded-md":
                        !subLink,
                      "xl:px-[24px] xl:py-[8px] lg:px-[15px] lg:py-[4px]  px-[18px] py-[8px] hover:bg-black hover:bg-opacity-10 hover:rounded-md":
                        subLink,
                      "text-white border-transparent":
                        !item.isActive && !subLink,
                      "text-[#3586FF] border-b-4 border-[#3586FF] ":
                        item.isActive && !subLink,
                    })}
                >
                  {item.name}
                </div>
              </Link>
            ) : (
              <>
                {item.subLink && item.subLink.length > 0 && (
                  <NavDropDown item={item} subLink={subLink} />
                )}
              </>
            )}
          </>
        </div>
      ))}
    </>
  );
};

export interface User {
  id: number;
  username?: string;
  fullName?: string;
  email: string;
  phone?: string;
  token?: string;
  roles: {
    id: string;
    roleName: string;
  };
}

export interface ShowItems {
  isVisibleItems: Boolean
}
const Navbar = ({ isVisibleItems }: ShowItems) => {
  const router = useRouter();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [user, setUser] = useState<User | null>();
  const [token, setToken] = useState<string | null>();
  const { items: wishListItems } = useWishlistStore((state) => state);

  const session = useSession();
  const { items } = useCartStore((state: any) => state);

  const toggleMobileSideBar = (flag: boolean) => {
    setMobileSidebarOpen(flag);
  };

  useEffect(() => {
    if (session.status === "authenticated") {
      const userData = session.data?.user as User | null;
      const userToken = userData?.token || localStorage.getItem("token");

      setUser(userData);
      setToken(userToken);

      // Store token in local storage for persistence
      if (userToken) {
        localStorage.setItem("token", userToken);
      }
    }
  }, [session]);

  const logo_place_holder = {
    imageUrl: "/images/logomain.png",
    link: "/",
  };

  const navMenuItems: INavItems[] = [
    {
      name: "Home",
      link: "/",
      isActive: router.pathname === "/",
      onClick: () => {
        if (mobileSidebarOpen) {
          toggleMobileSideBar(false);
        }
      },
    },
    {
      name: "Services",
      link: "/services/custom-builder",
      subLink: [
        {
          name: "Custom Builder",
          link: "/services/custom-builder",
          isActive: router.pathname === "/services/custom-builder",
        },
        {
          name: "Home Decor",
          link: "/services/custom-builder/homedecor",
          isActive: router.pathname === "/services/custom-builder/homedecor",
        },
        {
          name: "Electronics",
          link: "/services/custom-builder/electronics",
          isActive: router.pathname === "/services/custom-builder/electronics",
        },
        {
          name: "Interiors",
          link: "/interiors",
          isActive: router.pathname === "/interiors",
        },
        {
          name: "Furniture",
          link: "/services/custom-builder/furnitures",
          isActive: router.pathname === "/services/custom-builder/furnitures",
        },
        {
          name: "Home Loan",
          link: "/services/custom-builder/loans",
          isActive: router.pathname === "/services/custom-builder/loans",
        },
        {
          name: "Plumbing",
          link: "/services/custom-builder/plumbing",
          isActive: router.pathname === "/services/custom-builder/plumbing",
        },
      ],
      isActive: router.pathname.includes("/services"),
    },
    {
      name: "Properties",
      link: "/properties",
      isActive: router.pathname === "/properties",
    },
    {
      name: "Blogs",
      link: "/blogs",
      isActive: router.pathname === "/blogs",
      onClick: () => {
        if (mobileSidebarOpen) {
          toggleMobileSideBar(false);
        }
      },
    },
    {
      name: "Contact Us",
      link: "/contact-us",
      isActive: router.pathname === "/contact-us",
      onClick: () => {
        if (mobileSidebarOpen) {
          toggleMobileSideBar(false);
        }
      },
    },
    {
      name: 'Refer & Earn',
      link: "/referandearn",
      isActive: router.pathname === "/referandearn",
      onClick: () => {
        if (mobileSidebarOpen) {
          toggleMobileSideBar(false);
        }
      },

    },
    {
      name: "Packages",
      link: "/packages",
      subLink: [
        {
          name: "Broker",
          link: "/packages?tab=broker",
          isActive: router.pathname === "/packages?tab=broker",
        },
        {
          name: "Developer",
          link: "/packages?tab=developer",
          isActive: router.pathname === "/packages?tab=developer",
        },
      ],
      isActive: router.pathname.startsWith("/packages"),
    }
  ];

  const handlePostProperty = () => {
    if (session.status !== "authenticated") {
      router.push("/post-property");
    } else {
      router.push("/post-property/details");
    }
  };

  const RenderNav = () => (

    <div className="lg:pt-[0px] pt-[55px] flex lg:h-full gap-9 lg:gap-[6px] flex-col lg:flex-row lg:items-center">
      {isVisibleItems ? (<WebNavigationMenu items={navMenuItems} subLink={false} />) :
        ""}
    </div>
  );

  return (
    <>
      <div className="min-h-[50px] sticky z-[999] flex w-full items-center inset-x-0 top-0 bg-[#081221] py-[4px] ">

        <div className="hidden lg:flex w-full h-full items-center justify-between xl:px-10 lg:px-5">
          <Link
            className="relative min-w-[187px] min-h-[40px]"
            href={logo_place_holder.link}
          >
            <div className="flex flex-row">
              <div className=" relative  md:h-[50px] md:w-[50px] ">
                <Image
                  src={logo_place_holder.imageUrl}
                  alt={`source_image`}
                  className="absolute object-contain"
                  fill
                />
              </div>
              <div className="xl:ml-2 flex flex-col items-center gap-1 justify-center">
                <p className="font-Gordita-Bold xl:text-[20px] lg:text-[20px] text-[#5297FF]">
                  DREAM
                  <span className="text-white">CASA</span>
                </p>
                <p className="text-[10px] text-center mt-[-10px] text-white">
                  Right Path to Dream Key
                </p>
              </div>
            </div>
          </Link>
          <div>
            <RenderNav />
          </div>
          {
            <div className="flex items-center xl:gap-5 lg:gap-3">
              <Button
                onClick={handlePostProperty}
                className="py-[4px] px-[10px] bg-white text-[#000000] font-Gordita-Medium rounded  "
              >
                <div className="flex flex-row gap-2">
                  <p className="text-nowrap text-[14px]">Post property</p>
                  <p
                    style={{
                      background:
                        "linear-gradient(99.41deg, #3586FF 7.11%, #205199 98.13%)",
                    }}
                    className="text-xs mt-0.5 w-[50px] max-h-[25px] rounded px-2 py-0.5 leading-[17px] text-white"
                  >
                    Free
                  </p>
                </div>
              </Button>
              <div className="flex  relative flex-row gap-2 justify-center items-center ">
                {user && (
                  <>
                    <Link
                      className="cursor-pointer p-[10px] relative"
                      href={"/user/wishlist"}
                      aria-label="Go to wishlist"
                    >
                      <IoHeart className="text-white text-[24px]" />
                      {wishListItems.length > 0 && (
                        <span className="absolute text-[8px] top-[4px] right-[4px] px-[5px] py-[2px] rounded-full bg-red-600 text-white">
                          {wishListItems.length}
                        </span>
                      )}
                    </Link>
                    <Link
                      className="cursor-pointer p-[10px]"
                      href={"/cart"}
                      aria-label="Go to cart"
                    >
                      <IoCartOutline className="text-white text-[24px]" />
                    </Link>
                  </>
                )}
                <p
                  className={`${items.length === 0 && "hidden"
                    } text-white font-Gordita-Medium md:text-[12px] text-[10px] absolute -top-[2px]  -right-[2px] px-[8px] py-[2px] rounded-full bg-blue-500`}
                >
                  {items.length}
                </p>
              </div>
              {!user || !token ? (
                <Link href={`/login`}>
                  <Button className="py-[4px] px-[18px] md:text-[14px] bg-[#5297FF] font-Gordita-Medium text-white rounded">
                    Login
                  </Button>
                </Link>
              ) : (
                <div className="cursor-pointer">
                  <Avatar />
                </div>
              )}
            </div>
          }
        </div>
        {/* for mobile view */}

        <div className="flex lg:hidden w-full h-full items-center justify-between px-6">
          <Link
            className="relative xl:min-w-[187px] xl:min-h-[40px]"
            href={logo_place_holder.link}
          >
            <div className=" relative h-[40px] w-[40px] pr-[10px]">
              <Image
                src={logo_place_holder.imageUrl}
                alt={`source_image`}
                className="absolute object-cover"
                fill
              />
            </div>
          </Link>
          <div className=" ml-[10px] flex items-center md:justify-start justify-center gap-x-[2px] ">
            <Button
              onClick={handlePostProperty}
              aria-label="Post property"
              className="md:py-[8px] py-[2px] md:px-[10px] px-[8px] bg-white text-[#000000] font-Gordita-Medium rounded  "
            >
              <div className="flex flex-row items-center gap-2">
                <p className="text-nowrap md:text-[16px] text-[10px]">Post property</p>
                <p
                  style={{
                    background:
                      "linear-gradient(99.41deg, #3586FF 7.11%, #205199 98.13%)",
                  }}
                  className="text-xs mt-0.5 w-[50px] md:text-[16px] text-[10px] max-h-[25px] rounded md:px-2 px-1 py-0.5 leading-[17px] text-white"
                >
                  Free
                </p>
              </div>
            </Button>
            <div className="flex  relative flex-row gap-2 justify-center items-center ">
              <Link
                className="cursor-pointer p-[10px]"
                href={"/cart"}
                aria-label="Go to cart"
              >
                <IoCartOutline className="text-white md:text-[24px] text-[22px]" />
              </Link>
              <p
                className={`${items?.length === 0 && "hidden"
                  } text-white md:text-[16px] text-[10px] absolute -top-[0px] right-[3px] md:px-[7px] px-[5px] py-[2px] rounded-full bg-blue-500`}
              >
                {items?.length}
              </p>
            </div>
          </div>
          <RxHamburgerMenu
            className="text-white cursor-pointer"
            onClick={() => {
              toggleMobileSideBar(true);
            }}
          />
        </div>
      </div>

      {mobileSidebarOpen && (
        <Drawer
          open={mobileSidebarOpen}
          handleDrawerToggle={toggleMobileSideBar}
          panelCls="bg-[#18213F] sm:w-[85%] lg:hidden"
          overLayCls="bg-white bg-opacity-30"
          closeOnOutsideClick
          openVariant="right"
          hideHeader
        >
          <div className="relative h-full lg:hidden block ">
            <RenderNav />

            <div className="absolute bottom-0 left-0 w-full ">
              {!user || !token ? (
                <Link href={`/login`}>
                  <Button className="md:py-[8px] py-[12px] md:px-[18px] px-[27px] bg-[#5297FF] text-white rounded">
                    Login
                  </Button>
                </Link>
              ) : (
                <div className="cursor-pointer">
                  <Avatar />
                </div>
              )}
            </div>
          </div>
        </Drawer>
      )}
    </>
  );
};

export default Navbar;
